import { useMemo } from 'react';

import type {
    SchemaChatResolver_GetChats,
    SchemaPartnerResolver_GetMyPartnerContact,
} from 'cpool-tables/backend-resolvers';
import type { ParseKeys } from 'i18next';
import { useTranslation } from 'react-i18next';

import { useOwnDivisionPhoneNumberId } from '~/features/whatsapp/hooks/useOwnDivisionPhoneNumberId';
import { usePartnerContacts } from '~/features/whatsapp/hooks/usePartnerContacts';
import { useStartPartnerChatMutation } from '~/features/whatsapp/hooks/useStartPartnerChatMutation';
import { CHAT_CONTACT_TABS, openPanel, useChatPanelStore } from '~/features/whatsapp/stores/chatPanelStore';
import { isPartnerChat } from '~/features/whatsapp/utils/isPartnerChat';

type Params = {
    allChats: SchemaChatResolver_GetChats | undefined;
    isLeadScoped: boolean;
    search: string;
    clearSearch: () => void;
};

export const usePartnerChatTabs = ({ allChats, clearSearch, isLeadScoped, search }: Params) => {
    const { t } = useTranslation();
    const contactTab = useChatPanelStore.use.contactTab();
    const { data: partnerContacts = [], isError: isPartnerContactsError } = usePartnerContacts();
    const startPartnerChat = useStartPartnerChatMutation();
    const ownPhoneNumberId = useOwnDivisionPhoneNumberId();

    const hasPartnerChats = useMemo(() => (allChats ?? []).some(isPartnerChat), [allChats]);
    // Tabs if contacts, partner chats, or contacts error (don't hide failure).
    const showContactTabs = !isLeadScoped && (partnerContacts.length > 0 || hasPartnerChats || isPartnerContactsError);
    const isPartnerTab = showContactTabs && contactTab === CHAT_CONTACT_TABS.PARTNERS;

    // "Kontakte": assigned partners matching the search without an active (non-archived) chat on the own number yet.
    const contactRows = useMemo(() => {
        const searchLower = search.trim().toLowerCase();
        if (!isPartnerTab || !searchLower) return [];
        const partnerIdsWithChat = new Set(
            (allChats ?? [])
                .filter((chat) => !chat.resolved && (!ownPhoneNumberId || chat.phoneNumberId === ownPhoneNumberId))
                .flatMap((chat) => chat.partnerIds),
        );
        return partnerContacts.filter(
            (contact) => !partnerIdsWithChat.has(contact.id) && contact.name.toLowerCase().includes(searchLower),
        );
    }, [allChats, isPartnerTab, ownPhoneNumberId, partnerContacts, search]);
    // Partner search splits results into "Chats" and "Kontakte" sections.
    const showSections = isPartnerTab && search.trim().length > 0;

    // No reason shown while pending — a stale-click rejection would flash a reason for an about-to-succeed click.
    const contactDisabledReason =
        !startPartnerChat.canStart && !startPartnerChat.isPending && startPartnerChat.disabledReasonCode
            ? t(`WHATSAPP_CHAT.ERROR.${startPartnerChat.disabledReasonCode}` as ParseKeys)
            : undefined;

    const handleContactClick = (contact: SchemaPartnerResolver_GetMyPartnerContact) => {
        startPartnerChat
            .mutateAsync({ partnerId: contact.id, phoneE164: contact.phoneE164 })
            .then(({ chatId }) => {
                clearSearch();
                openPanel({ autoOpenTemplatePicker: true, chatId });
            })
            // Command errors are already toasted by useStartPartnerChatMutation's onError.
            .catch(() => undefined);
    };

    return {
        contactDisabledReason,
        contactRows,
        contactsDisabled: !startPartnerChat.canStart || startPartnerChat.isPending,
        contactTab,
        handleContactClick,
        isPartnerContactsError,
        isPartnerTab,
        showContactTabs,
        showSections,
    };
};
